/**
 * Release 2 A — smart board definitions (S3) and system board presets (S4), docs/specs/release2-contract.md.
 *
 * A definition is a `SearchFilters` object: the watched scope (`board_ids` + `include_descendants`, empty = every
 * board the viewer can see), the tidy predicates (`untagged` / `unfiled` / `incomplete`) and every other predicate the
 * board filter bar edits. `include_deleted` is never sent (the API answers 422 when the key is present). Pure helpers.
 */
import type { SearchFilters, SearchRequest } from '@/lib/api/types';
import { compactBoardFilters, type BoardFilters } from '@/features/search/predicates/serialize';

export const TIDY_KEYS = ['untagged', 'unfiled', 'incomplete'] as const;
export type TidyKey = (typeof TIDY_KEYS)[number];
export const TIDY_LABEL: Record<TidyKey, string> = { untagged: 'Untagged', unfiled: 'Unfiled', incomplete: 'Incomplete' };
export const TIDY_HINT: Record<TidyKey, string> = {
  untagged: 'no tag added by a person (AI suggestions do not count)',
  unfiled: 'not in any sub-board other than an Inbox',
  incomplete: 'a required field is empty',
};

export type SmartScope = { boardIds: string[]; includeDescendants: boolean };
export type SmartTidy = Partial<Record<TidyKey, true>>;
/** The three parts the dialog edits separately. */
export type SmartDraft = { scope: SmartScope; tidy: SmartTidy; rest: BoardFilters };

export const SMART_SORTS = [
  { id: 'created_at:desc', label: 'Newest' },
  { id: 'created_at:asc', label: 'Oldest' },
  { id: 'name:asc', label: 'Name A–Z' },
  { id: 'name:desc', label: 'Name Z–A' },
  { id: 'size:desc', label: 'Largest' },
  { id: 'size:asc', label: 'Smallest' },
] as const;
export type SmartSort = (typeof SMART_SORTS)[number]['id'];
export const DEFAULT_SMART_SORT: SmartSort = 'created_at:desc';

/** Split a stored or proposed definition (any `SearchFilters`-like object) into its editable parts. */
export function splitDefinition(f: Partial<SearchFilters> | Record<string, unknown> | null | undefined): SmartDraft {
  const src = (f ?? {}) as Record<string, unknown>;
  const boardIds = Array.isArray(src.board_ids) ? (src.board_ids as unknown[]).filter((x): x is string => typeof x === 'string') : [];
  const tidy: SmartTidy = {};
  for (const k of TIDY_KEYS) if (src[k] === true) tidy[k] = true;
  const rest = { ...src };
  for (const k of TIDY_KEYS) delete rest[k];
  return { scope: { boardIds: [...new Set(boardIds)], includeDescendants: src.include_descendants !== false }, tidy, rest: compactBoardFilters(rest as Partial<SearchFilters>) };
}

/** The `filters` body of `createSmartBoard` / `updateSmartBoardDefinition`. */
export function joinDefinition(d: SmartDraft): SearchFilters {
  const out: Record<string, unknown> = { ...compactBoardFilters(d.rest) };
  for (const k of TIDY_KEYS) if (d.tidy[k]) out[k] = true;
  if (d.scope.boardIds.length) {
    out.board_ids = d.scope.boardIds;
    out.include_descendants = d.scope.includeDescendants;
  }
  return out as SearchFilters;
}

/** Proposed definition for "Save as smart board" on a board: that board (and its sub-boards when `deep`) + its filters. */
export function definitionFromBoard(boardId: string, deep: boolean, filters: BoardFilters): SmartDraft {
  return { ...splitDefinition(filters), scope: { boardIds: [boardId], includeDescendants: deep } };
}

/** Proposed definition for "Save as smart board" on the search page (the free text is not part of a definition). */
export function definitionFromSearch(filters: SearchFilters): SmartDraft {
  return splitDefinition(filters);
}

/** Number of predicates in a definition, scope included (a definition with none matches every visible asset). */
export function countDefinition(d: SmartDraft): number {
  const rest = compactBoardFilters(d.rest) as Record<string, unknown>;
  let n = d.scope.boardIds.length ? 1 : 0;
  n += TIDY_KEYS.filter((k) => d.tidy[k]).length;
  for (const [k, v] of Object.entries(rest)) if (k !== 'tag_match' && k !== 'include_archived') n += Array.isArray(v) ? v.length : 1;
  return n;
}

// --- system boards ----------------------------------------------------------------------------------
/** Display names by key (the API sends English names; the web owns the labels). Order = API order. */
export const SYSTEM_BOARD_LABEL: Record<string, string> = {
  untagged: 'Untagged',
  incomplete: 'Incomplete',
  unfiled: 'Unfiled',
  needs_review: 'Needs review',
  expiring: 'Expiring soon',
  duplicates: 'Duplicates',
};
export const SYSTEM_BOARD_HINT: Record<string, string> = {
  untagged: 'Assets without a tag added by a person',
  incomplete: 'Assets with an empty required field',
  unfiled: 'Assets that sit only in a top-level board or an Inbox',
  needs_review: 'Assets waiting for approval',
  expiring: 'Usage rights that end in the next 30 days',
  duplicates: 'Exact and near-duplicate groups',
};

/** The search page asks for the `tidy` facet on top of `ALL_FACETS` (features/search/url.ts). */
export function withTidyFacet(r: SearchRequest | null): SearchRequest | null {
  if (!r || r.facets?.includes('tidy')) return r;
  return { ...r, facets: [...(r.facets ?? []), 'tidy'] };
}
